import { z } from 'zod';
import { AccountIdSchema, RecordKeySchema } from '../ids.js';
import {
  AwsConnectionInputSchema,
  AwsConnectionSchema,
  CodeDetailSchema,
  CodeRunSchema,
  CodeStartSchema,
  CodeTaskSchema,
  CodeWorkspaceSchema,
} from '../rumi-code.js';
import { CodeApprovalSchema } from '../api/rumi-code.js';
import type { CommandDef } from './commands.js';
/** Write-only IPC input. These values never belong in API routes, connection metadata or events. */
export const LocalAwsCredentialsSchema = z.object({
  accessKeyId: z.string().regex(/^[A-Z0-9]{16,128}$/),
  secretAccessKey: z.string().min(16).max(256),
  sessionToken: z.string().min(1).max(16384).optional(),
  expiresAt: z.string().datetime().nullable(),
  principalArn: z.string().regex(/^arn:aws:iam::\d{12}:(?:role|user)\/[A-Za-z0-9+=,.@_/-]+$/),
});
export type LocalAwsCredentials = z.infer<typeof LocalAwsCredentialsSchema>;
export const LocalAwsCredentialStatusSchema = z.object({
  connected: z.boolean(),
  principalArn: z.string().nullable(),
  expiresAt: z.string().datetime().nullable(),
});
const account = z.object({ accountId: AccountIdSchema });
const run = account.extend({ runId: z.string().uuid() });
const base = { scope: 'account' as const, capability: 'rumi_code.read' as const, allowLocked: false, localModeOnly: false };
export const rumiCodeCommands = {
  'rumicode.credentialStatus': {
    ...base,
    capability: 'rumi_code.configure',
    input: account,
    output: LocalAwsCredentialStatusSchema,
    description: 'Read this client’s local AWS connection status, never its secrets.',
  },
  'rumicode.saveCredentials': {
    ...base,
    capability: 'rumi_code.configure',
    externalWrite: true,
    input: account.extend({ credentials: LocalAwsCredentialsSchema }),
    output: LocalAwsCredentialStatusSchema,
    description: 'Verify and encrypt this client’s AWS credentials on this Mac.',
  },
  'rumicode.forgetCredentials': {
    ...base,
    capability: 'rumi_code.configure',
    externalWrite: true,
    input: account,
    output: LocalAwsCredentialStatusSchema,
    description: 'Stop this client’s local AWS execution and forget its credentials.',
  },
  'rumicode.workspace': {
    ...base,
    input: account,
    output: CodeWorkspaceSchema.extend({ tasks: z.array(CodeTaskSchema) }),
    description: 'Provider task queue and AWS remediation progress.',
  },
  'rumicode.prepare': {
    ...base,
    capability: 'rumi_code.configure',
    externalWrite: true,
    input: account,
    output: AwsConnectionSchema,
    description: 'Prepare AWS role onboarding.',
  },
  'rumicode.configure': {
    ...base,
    capability: 'rumi_code.configure',
    externalWrite: true,
    input: account.extend({ connection: AwsConnectionInputSchema }),
    output: AwsConnectionSchema,
    description: 'Configure AWS execution scope.',
  },
  'rumicode.testConnection': {
    ...base,
    capability: 'rumi_code.configure',
    input: account,
    output: AwsConnectionSchema,
    description: 'Verify scoped AWS role access.',
  },
  'rumicode.iamPolicy': {
    ...base,
    capability: 'rumi_code.configure',
    input: account,
    output: z.object({ trust: z.json(), permissions: z.json(), instructions: z.string() }),
    description: 'Generate customer IAM role policies.',
  },
  'rumicode.start': {
    ...base,
    capability: 'rumi_code.run',
    externalWrite: true,
    input: account.extend({ request: CodeStartSchema }),
    output: CodeRunSchema,
    description: 'Start the selected AWS remediation.',
  },
  'rumicode.detail': { ...base, input: run, output: CodeDetailSchema, description: 'Load remediation progress and evidence.' },
  'rumicode.approve': {
    ...base,
    capability: 'rumi_code.approve',
    externalWrite: true,
    input: run.extend({ decision: CodeApprovalSchema }),
    output: CodeRunSchema,
    description: 'Approve or reject the saved remediation plan.',
  },
  'rumicode.cancel': {
    ...base,
    capability: 'rumi_code.run',
    externalWrite: true,
    input: run,
    output: CodeRunSchema,
    description: 'Cancel further AWS changes.',
  },
  'rumicode.recheck': {
    ...base,
    capability: 'grc.sync',
    startsCollection: true,
    input: run.extend({ refresh: z.boolean().optional() }),
    output: z.object({ status: z.string(), syncRunIds: z.array(z.string()) }),
    description: 'Refresh the source provider after verified AWS remediation.',
  },
  'rumicode.exportEvidence': {
    ...base,
    capability: 'artifact.export',
    input: run,
    output: z.object({ path: z.string() }),
    description: 'Export the complete remediation evidence bundle.',
  },
  'rumicode.uploadEvidence': {
    ...base,
    capability: 'rumi_code.run',
    externalWrite: true,
    input: run.extend({ targetId: z.string().max(500), workspaceId: z.number().int().positive().optional() }),
    output: z.object({ status: z.string(), evidenceId: z.string().nullable() }),
    description: 'Upload verified evidence to a provider-supported evidence destination.',
  },
} as const satisfies Record<string, CommandDef>;
